import * as React from "react"
import { useCallback, useState } from "react"
import { Skeleton, Button, Box } from "@mui/material"
import { TableSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import AddIcon from "@mui/icons-material/Add"

import { isLoadingSchedules, schedulesTableData, schedulesTableState, isCreateScheduleDrawerOpen, selectedScheduleForEdit, isEditScheduleDrawerOpen } from "../@signals"
import { useSchedulesData, useScheduleMutations } from "../@hooks"
import {
    idScheduleColumn,
    scheduleNameColumn,
    batchTypeColumn,
    frequencyColumn,
    isActiveColumn,
    nextExecutionColumn,
    lastExecutionColumn,
    createdAtColumn,
    actionsColumn,
    producedColumn,
    batchesColumn,
    unattemptedColumn,
    seenColumn,
} from "./columns"


export default function SchedulesTable() {
    const { isEmpty } = useSchedulesData()
    const { showToast } = useAppToast()
    const { deleteSchedule, toggleActive, runNow } = useScheduleMutations()

    const [loadingStates, setLoadingStates] = useState({
        deleting: null,
        toggling: null,
        running: null
    })

    const handleTableChange = useCallback((action, tableState) => {
        if (action === 'sort' || action === 'changePage' || action === 'changeRowsPerPage' || action === 'search') {
            schedulesTableState.value = {
                sortOrder: tableState.sortOrder || schedulesTableState.value.sortOrder,
                page: tableState.page ?? schedulesTableState.value.page,
                rowsPerPage: tableState.rowsPerPage ?? schedulesTableState.value.rowsPerPage,
                searchText: tableState.searchText ?? schedulesTableState.value.searchText
            }
        }
    }, [])

    const handleCreateSchedule = useCallback(() => {
        isCreateScheduleDrawerOpen.value = true
    }, [])

    const handleEdit = useCallback((id_schedule) => {
        const schedule = schedulesTableData.value.find(s => s.idSchedule === id_schedule)
        if (schedule) {
            selectedScheduleForEdit.value = schedule
            isEditScheduleDrawerOpen.value = true
        }
    }, [])

    const handleDelete = useCallback((id_schedule, scheduleName) => {
        showToast.confirm(
            `Are you sure you want to delete schedule "${scheduleName}"? This will not affect batches already created by this schedule.`,
            {
                onSubmit: async () => {
                    setLoadingStates(prev => ({ ...prev, deleting: id_schedule }))
                    try {
                        await deleteSchedule(id_schedule)
                    } finally {
                        setLoadingStates(prev => ({ ...prev, deleting: null }))
                    }
                }
            }
        )
    }, [showToast, deleteSchedule])

    const handleToggleActive = useCallback(async (id_schedule) => {
        setLoadingStates(prev => ({ ...prev, toggling: id_schedule }))
        try {
            await toggleActive(id_schedule)
        } finally {
            setLoadingStates(prev => ({ ...prev, toggling: null }))
        }
    }, [toggleActive])

    const handleRunNow = useCallback(async (id_schedule, scheduleName) => {
        showToast.confirm(
            `Run "${scheduleName}" now? This will create a new batch immediately.`,
            {
                onSubmit: async () => {
                    setLoadingStates(prev => ({ ...prev, running: id_schedule }))
                    try {
                        await runNow(id_schedule)
                    } finally {
                        setLoadingStates(prev => ({ ...prev, running: null }))
                    }
                }
            }
        )
    }, [showToast, runNow])

    const handleViewHistory = useCallback((id_schedule, scheduleName) => {
        // TODO: Implement history modal/drawer
        showToast.info(`History for "${scheduleName}" - Coming soon!`)
    }, [showToast])

    if (isLoadingSchedules.value) return <Skeleton variant="rectangular" height={500} sx={{ mt: 2 }} />

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleCreateSchedule}
                >
                    Create Schedule
                </Button>
            </Box>

            <TableSeeker
                data={schedulesTableData.value}
                title={'Schedules'}
                columns={[
                    idScheduleColumn(),
                    scheduleNameColumn(),
                    batchTypeColumn(),
                    frequencyColumn(),
                    isActiveColumn(),
                    nextExecutionColumn(),
                    lastExecutionColumn(),
                    // The detection pair, right after "when did it last run": together they
                    // answer "is this detector detecting". `batches`/`seen` are hidden
                    // companions the renderers read for their tooltips.
                    producedColumn(),
                    batchesColumn(),
                    unattemptedColumn(),
                    seenColumn(),
                    createdAtColumn(),
                    actionsColumn(
                        {
                            onEdit: handleEdit,
                            onDelete: handleDelete,
                            onToggleActive: handleToggleActive,
                            onRunNow: handleRunNow,
                            onViewHistory: handleViewHistory
                        },
                        loadingStates
                    )
                ]}
                options={{
                    selectableRows: 'none',
                    sortOrder: schedulesTableState.value.sortOrder,
                    page: schedulesTableState.value.page,
                    rowsPerPage: schedulesTableState.value.rowsPerPage,
                    searchText: schedulesTableState.value.searchText,
                    onTableChange: handleTableChange
                }}
                textLabels={{
                    body: {
                        noMatch: 'No schedules found.',
                    }
                }}
            />
        </Box>
    )
}
